import React, { useEffect, useState } from "react"
import { useSelector } from "react-redux"
import { Navigate, useLocation } from "react-router-dom"
import { getAdminHomePath } from "../../helpers/adminRoles"
import {
  fetchMandatoryProjectAcceptInvite,
  isMandatoryProjectAcceptDone,
} from "../../helpers/mandatoryProjectAccept"
import { isMemberUser } from "../../helpers/routeAccess"

const DASHBOARD_PATH = "/dashboard"

/**
 * Member app routes — only `member` type; others go to admin dashboard.
 * Pending mandatory project-accept forces /dashboard until completed.
 *
 * Invited-project API is checked once per user (not on every route like
 * project overview).
 */
const MemberMiddleware = ({ children }) => {
  const currentUser = useSelector(state => state.auth.currentUser)
  const location = useLocation()
  const [pendingAccept, setPendingAccept] = useState(null)
  const [checked, setChecked] = useState(false)

  useEffect(() => {
    let cancelled = false

    const check = async () => {
      if (!isMemberUser(currentUser)) {
        if (!cancelled) {
          setPendingAccept(null)
          setChecked(true)
        }
        return
      }

      if (Number(currentUser?.total_projects) !== 1) {
        if (!cancelled) {
          setPendingAccept(null)
          setChecked(true)
        }
        return
      }

      if (!cancelled) setChecked(false)
      const invite = await fetchMandatoryProjectAcceptInvite(currentUser)
      if (!cancelled) {
        setPendingAccept(invite)
        setChecked(true)
      }
    }

    check()
    return () => {
      cancelled = true
    }
    // Only re-check when user identity / project count changes — not on every
    // currentUser object reference update (would re-open the accept gate).
    // eslint-disable-next-line react-hooks/exhaustive-deps -- currentUser.id / total_projects
  }, [currentUser?.id, currentUser?.total_projects])

  // After accept, sessionStorage is marked done — drop the gate immediately
  // (including this render) so we don't bounce back to /dashboard and reopen
  // the modal before the effect clears state.
  const activePending =
    pendingAccept?.id &&
    currentUser?.id &&
    !isMandatoryProjectAcceptDone(currentUser.id, pendingAccept.id)
      ? pendingAccept
      : null

  useEffect(() => {
    if (pendingAccept && !activePending) {
      setPendingAccept(null)
    }
  }, [pendingAccept, activePending])

  if (!isMemberUser(currentUser)) {
    return <Navigate to={getAdminHomePath()} replace />
  }

  const onDashboard =
    location.pathname === DASHBOARD_PATH ||
    location.pathname === `${DASHBOARD_PATH}/`

  if (!checked && Number(currentUser?.total_projects) === 1 && !onDashboard) {
    return null
  }

  if (checked && activePending && !onDashboard) {
    return (
      <Navigate
        to={DASHBOARD_PATH}
        replace
        state={{ initialAcceptInvite: activePending }}
      />
    )
  }

  return children
}

export default MemberMiddleware
